import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface WatchdogHeartbeat {
  name: string;
  last_run_at: string;
  last_status: string | null;
  last_issues_count: number;
  expected_interval_seconds: number;
}

interface WatchdogHealthResult {
  name: string;
  isAlive: boolean;
  ageSeconds: number;
  lastStatus: string | null;
  lastIssuesCount: number;
  expectedIntervalSeconds: number;
}

const QUERY_KEY = ['watchdog-heartbeats'];

// A watchdog is "dead" if its last heartbeat is older than 5x its expected interval.
// At 60s expected, that's 5 minutes. Conservative — gives pg_cron room for transient
// hiccups without alarming the user.
const DEAD_MULTIPLIER = 5;

export function useWatchdogHealth() {
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<WatchdogHealthResult[]> => {
      // Table is too new for the generated types — cast to any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('watchdog_heartbeats')
        .select('*');

      if (error) {
        console.error('Failed to fetch watchdog heartbeats:', error);
        return [];
      }

      const now = Date.now();
      return (data as WatchdogHeartbeat[]).map((row) => {
        const ageSeconds = Math.floor((now - new Date(row.last_run_at).getTime()) / 1000);
        const deadThreshold = row.expected_interval_seconds * DEAD_MULTIPLIER;
        return {
          name: row.name,
          isAlive: ageSeconds <= deadThreshold,
          ageSeconds,
          lastStatus: row.last_status,
          lastIssuesCount: row.last_issues_count,
          expectedIntervalSeconds: row.expected_interval_seconds,
        };
      });
    },
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });

  const watchdogs = data ?? [];
  const deadWatchdogs = watchdogs.filter((w) => !w.isAlive);

  return {
    watchdogs,
    deadWatchdogs,
    hasDeadWatchdog: deadWatchdogs.length > 0,
    isLoading,
  };
}
